import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { linkToOffer, signedInWithName } from '../services/signInLink';
import { openLinkConsent } from '../services/signInSheet';
import {
    clearSecondAccountHint, hintFor, subscribeSecondAccountHint, trackHint,
} from '../services/secondAccountHint';
import { logout } from '../services/authService';

// What the sign-in sheet asks once a student is signed in (#5154, founder
// pick A of the "more ways to sign in" mocks). Mounted once at the app root,
// beside <SignInSheet />.

const GREEN = '#1A4D2E';

/** After the student signed in the way they did before, "Also sign in with
 *  Apple next time?" when an account-exists answer left a credential for
 *  this very account (signInLink.linkToOffer). */
export function SignInLinkOffer() {
    const { currentUser } = useAuth() || {};
    useEffect(() => {
        if (!currentUser) return;
        const pending = linkToOffer(currentUser);
        if (pending) openLinkConsent({ pending, user: currentUser, signedInWith: signedInWithName(currentUser) });
    }, [currentUser]);
    return null;
}

/** "Used Stratia before with Google or your email? Sign out and use that
 *  way." Shown once, on the first screen after a new Apple account with a
 *  relay address or a new Facebook account (secondAccountHint.js). */
export function SecondAccountHint() {
    const { currentUser } = useAuth() || {};
    const tick = useSyncExternalStore(subscribeSecondAccountHint, () => localStorageStamp(), () => 0);
    const [hint, setHint] = useState(null);

    useEffect(() => {
        const h = hintFor(currentUser?.uid);
        if (!h) return;
        // Once: cleared as soon as it is on screen.
        clearSecondAccountHint();
        setHint(h);
        trackHint(h.method, 'shown');
    }, [currentUser?.uid, tick]);

    if (!hint || !currentUser) return null;
    const signOut = () => { trackHint(hint.method, 'tapped'); logout(); };
    return (
        <div className="fixed inset-x-0 top-3 z-[90] flex justify-center px-4" data-testid="signin-second-account-hint">
            <div role="status" className="relative w-full max-w-md rounded-2xl bg-white px-4 pb-4 pt-3 shadow-lg ring-1 ring-black/5">
                <p className="mb-1.5 pr-10 font-serif text-[17px] font-bold" style={{ color: GREEN }}>Welcome to Stratia</p>
                <p className="rounded-xl border border-[#C7DACB] bg-[#E6F0E8] px-3 py-2.5 text-[13.5px] text-[#22342A]">
                    Used Stratia before with Google or your email?{' '}
                    <button type="button" onClick={signOut} data-testid="signin-second-account-signout"
                        className="font-semibold underline underline-offset-2">Sign out</button>{' '}
                    and use that way.
                </p>
                <button type="button" onClick={() => setHint(null)} aria-label="Close"
                    className="absolute right-1 top-1 grid h-11 w-11 place-items-center rounded-full text-[#5D6A60] hover:bg-[#F3F1EA]">
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
            </div>
        </div>
    );
}

// A changing value for useSyncExternalStore when a hint is marked in this tab.
let stamp = 0;
subscribeSecondAccountHint(() => { stamp += 1; });
const localStorageStamp = () => stamp;
